import React from 'react'
import { redirect } from 'react-router-dom'
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles'
import AppBar from '@material-ui/core/AppBar'
import Toolbar from '@material-ui/core/Toolbar'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import Badge from '@material-ui/core/Badge'
import MenuItem from '@material-ui/core/MenuItem'
import Menu from '@material-ui/core/Menu'
import AccountCircle from '@material-ui/icons/AccountCircle'
import NotificationsIcon from '@material-ui/icons/Notifications'
import MoreIcon from '@material-ui/icons/MoreVert'
import MenuIcon from '@material-ui/icons/Menu'
import { UserContextType } from '../../../schemas/User'
import UserContext, { initialContextUserState } from '../../../contexts/User'
import { urls } from '../../../urls'
import UserService from '../../../services/User/index'
import NotificationsDrawer, { notificaciones } from '../NotificationsDrawer/NotificationsDrawer'
import ChangePasswordConfirmDialog from './ChangePasswordConfirmDialog'

// Número de WhatsApp con código de país (sin +). Ej: 5491112345678
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`
const WHATSAPP_ICON_SRC = `${process.env.PUBLIC_URL || ''}/whatsapp.png`
const LOGO_CRECER_SRC = `${process.env.PUBLIC_URL || ''}/images/logotipo-crecer-dmc.svg`

// -------------- Styles --------------
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    grow: {
      flexGrow: 1,
    },
    appBar: {
      zIndex: theme.zIndex.appBar,
    },
    toolBar: {
      background: '#ffff',
      color: theme.palette.text.primary,
      minHeight: 56,
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      [theme.breakpoints.up('sm')]: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
      },
    },
    greeting: {
      // minWidth: 0 permite que el saludo se trunque con ellipsis en pantallas angostas
      minWidth: 0,
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(1),
      [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(1),
      },
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.text.primary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    logo: {
      height: 24,
      width: 'auto',
      marginRight: theme.spacing(1.5),
      flexShrink: 0,
    },
    greetingText: {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    sectionDesktop: {
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'flex',
      },
    },
    sectionMobile: {
      display: 'flex',
      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
    },
    whatsappIcon: {
      padding: 6,
      '& img': {
        width: 28,
        height: 28,
        display: 'block',
      },
    },
  }),
)

interface AppBarProps {
  // mobile: muestra el botón hamburguesa que abre el menú lateral
  showMenuButton?: boolean
  onMenuClick?: () => void
}

export default function AppBarComponent({ showMenuButton = false, onMenuClick }: AppBarProps) {
  const classes = useStyles()
  const { userContext, setUserContext } = React.useContext(
    UserContext
  ) as UserContextType
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState<null | HTMLElement>(null)
  const [notificacionesDrawerOpen, setNotificacionesDrawerOpen] = React.useState(false)
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = React.useState(false)

  const isMenuOpen = Boolean(anchorEl)
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl)

  const openNotificacionesDrawer = () => {
    handleMobileMenuClose()
    setNotificacionesDrawerOpen(true)
  }

  const closeNotificacionesDrawer = () => {
    setNotificacionesDrawerOpen(false)
  }

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null)
  }

  const closeAccountMenu = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    closeAccountMenu()
    handleMobileMenuClose()
    const userService = new UserService()
    await userService.logout()
    setUserContext(initialContextUserState)
    redirect(urls.page.login)
  }

  const openChangePasswordDialog = () => {
    closeAccountMenu()
    handleMobileMenuClose()
    setChangePasswordDialogOpen(true)
  }

  const closeChangePasswordDialog = () => {
    setChangePasswordDialogOpen(false)
  }

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget)
  }

  const menuId = 'primary-search-account-menu'
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={closeAccountMenu}
    >
      <MenuItem onClick={openChangePasswordDialog}>Cambiar contraseña</MenuItem>
      <MenuItem onClick={handleLogout}>Cerrar sesión</MenuItem>
    </Menu>
  )

  const mobileMenuId = 'primary-search-account-menu-mobile'
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem
        component="a"
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <IconButton aria-label="Contactar por WhatsApp" size="small" component="span" disableRipple className={classes.whatsappIcon}>
          <img src={WHATSAPP_ICON_SRC} alt="WhatsApp" />
        </IconButton>
        <p>WhatsApp</p>
      </MenuItem>
      <MenuItem onClick={openNotificacionesDrawer}>
        <IconButton aria-label="mostrar notificaciones" color="inherit" size="small">
          <Badge badgeContent={notificaciones.length} color="secondary">
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <p>Notificaciones</p>
      </MenuItem>
      <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
        <p>Perfil</p>
      </MenuItem>
    </Menu>
  )

  return (
    <>
      <AppBar position="sticky" elevation={0} className={classes.appBar}>
        <Toolbar className={classes.toolBar}>
          {showMenuButton && (
            <IconButton
              edge="start"
              aria-label="Abrir menú"
              onClick={onMenuClick}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
          )}
          <div className={classes.greeting}>
            <img src={LOGO_CRECER_SRC} alt="logotipo de crecer dmc" className={classes.logo} />
            <Typography variant="body1" component="span" className={classes.greetingText}>
              Hola,{' '}{userContext.name} {userContext.lastname}
            </Typography>
          </div>
          <div className={classes.grow} />
          <div className={classes.sectionDesktop}>
            <IconButton
              aria-label="Contactar por WhatsApp"
              component="a"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.whatsappIcon}
            >
              <img src={WHATSAPP_ICON_SRC} alt="WhatsApp" />
            </IconButton>
            <IconButton aria-label="mostrar notificaciones" color="inherit" onClick={() => setNotificacionesDrawerOpen(true)}>
              <Badge badgeContent={notificaciones.length} color="secondary">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <IconButton
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
          </div>
          <div className={classes.sectionMobile}>
            <IconButton
              aria-label="show more"
              aria-controls={mobileMenuId}
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              color="inherit"
            >
              <MoreIcon />
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>
      <NotificationsDrawer
        open={notificacionesDrawerOpen}
        onClose={closeNotificacionesDrawer}
      />
      {renderMobileMenu}
      {renderMenu}
      <ChangePasswordConfirmDialog
        open={changePasswordDialogOpen}
        onClose={closeChangePasswordDialog}
      />
    </>
  )
}
